import { watch } from 'vue'
import { onBeforeRouteUpdate, onBeforeRouteLeave, useRoute } from 'vue-router/composables'
import { NavigationGuardNext, Route } from 'vue-router'

/** 
Depends on what we click, onBeforeRouteLeave and onBeforeRouteUpdate can be both run at the same time
Therefore, we need to guard against double-handling navigations (by using isNavigating flag)
For example: From the job sidesheet `jobs/1234`
   1. Clicking a link to another job `jobs/5678` will fire onBeforeRouteUpdate, but not onBeforeRouteLeave (because Vue is reusing the same component)
   2. Clicking "close" button to go back to job list `jobs/` will fire BOTH onBeforeRouteLeave AND onBeforeRouteUpdate.
      This is because closing the sidesheet causes the route to change (firing onBeforeRouteLeave), 
      and Vue is reusing the same parent component (`jobs/` endpoint), so onBeforeRouteUpdate is also fired.
   3. Clicking a link to an external page (e.g., navigating away from the app) will only fire onBeforeRouteLeave
   4. Clicking "Map" or "Schedule" view tabs will only fire onBeforeRouteLeave
Therefore, in any case, we want to ensure that our navigation guard logic (@options.shouldAllowNavigation) is only executed once per navigation action.
 
 * @param options - Configuration options
 * @param options.shouldAllowNavigation - Function to determine if navigation is allowed
 * @param options.shouldSkipGuard - Function to determine if the guard should be skipped (i.e., options.shouldAllowNavigation not called)
 * @param options.onNavigationStart - Optional callback after navigation is allowed and starts
*/
export const useJobNavigationGuard = (options: {
  shouldAllowNavigation: (to: Route) => boolean
  shouldSkipGuard: (to: Route) => boolean
  onNavigationStart?: () => void
}) => {
  const route = useRoute()
  let isNavigating = false

  const handleNavigation = (to: Route, next: NavigationGuardNext) => {
    // Prevent double-handling
    if (isNavigating) {
      next()
      return
    }

    // Skip guard for certain routes/states
    if (options.shouldSkipGuard(to)) {
      next()
      return
    }

    // Check if navigation should be allowed
    if (!options.shouldAllowNavigation(to)) {
      next(false)
      isNavigating = false
      return
    }

    // Mark as navigating and trigger optional callback
    isNavigating = true
    options.onNavigationStart?.()
    next()
  }

  // Register both guards
  onBeforeRouteUpdate((to, _from, next) => {
    handleNavigation(to, next)
  })

  onBeforeRouteLeave((to, _from, next) => {
    handleNavigation(to, next)
  })

  // Reset flag when route changes
  watch(
    () => route.fullPath,
    () => {
      isNavigating = false
    },
  )
}
